import { useMemo, useState } from "react";
import { Navigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { addDays } from "date-fns";
import { api } from "../lib/api";
import { ymd } from "../lib/date";
import type { RequestField, TimeOff } from "../lib/types";
import { Button } from "../components/ui/Button";
import { Field, Textarea } from "../components/ui/Input";
import { Modal } from "../components/ui/Modal";
import {
  RequestFieldList, formatRequestFieldValue,
} from "../components/RequestFields";
import { useEffectiveUser } from "../lib/auth";
import { can } from "../lib/permissions";
import { visibleRequestsFor } from "../lib/requestVisibility";

// Generic page for a custom form (Settings → Forms). Route:
// `/forms/:slug`. Renders the form's field catalogue for submission,
// lists the current agency's submissions of that form, and lets
// L2+ approve or deny each pending row. Notification wiring re-
// uses the existing time-off request path — supervisors get
// pinged on new submissions and the submitter gets pinged on
// approve/deny.
export function FormsPage() {
  const { slug: rawSlug } = useParams<{ slug: string }>();
  const slug = (rawSlug ?? "").trim();
  const me = useEffectiveUser();
  const canReview = can.editRoster(me);

  const forms = useQuery({ queryKey: ["forms"], queryFn: () => api.listForms() });
  const agency = useQuery({ queryKey: ["agency"], queryFn: api.getAgency });
  const form = useMemo(
    () => (forms.data ?? []).find((f) => f.slug === slug) ?? null,
    [forms.data, slug],
  );

  if (forms.isLoading || agency.isLoading) {
    return <div className="p-6 text-sm text-slate-500">Loading form…</div>;
  }
  const disabled = new Set(agency.data?.disabledFeatures ?? []);
  if (!form || form.builtin || disabled.has(form.slug)) {
    // Built-in forms have their own dedicated pages (Time off,
    // Training). Custom forms that have been toggled off in
    // Sections & display are effectively hidden — a direct URL
    // still bounces the caller back to the dashboard.
    return <Navigate to="/" replace />;
  }
  return <FormsPageBody slug={slug} formName={form.name} canReview={canReview} me={me} />;
}

function FormsPageBody({
  slug, formName, canReview, me,
}: {
  slug: string;
  formName: string;
  canReview: boolean;
  me: ReturnType<typeof useEffectiveUser>;
}) {
  const qc = useQueryClient();
  const start = ymd(addDays(new Date(), -30));
  const end = ymd(addDays(new Date(), 365));

  const fields = useQuery({
    queryKey: ["requestFields", slug],
    queryFn: () => api.listRequestFields(slug),
  });
  const items = useQuery({
    queryKey: ["timeoff", start, end],
    queryFn: () => api.listTimeOff(start, end),
  });
  const employees = useQuery({
    queryKey: ["employees"],
    queryFn: () => api.listEmployees(),
  });
  const empById = useMemo(
    () => new Map((employees.data ?? []).map((e) => [e.id, e])),
    [employees.data],
  );

  const visible = useMemo(() => {
    // Owner / L1 see every submission for this form across the
    // agency. L2 sees only submissions filed by officers whose home
    // department matches theirs (mirrors the notification fan-out
    // for custom forms). L3 / L4 see only their own.
    const scoped = (items.data ?? []).filter((t) => t.formSlug === slug);
    return visibleRequestsFor(me, scoped, empById);
  }, [items.data, slug, me, empById]);

  const pending = visible
    .filter((t) => t.status === "requested" || t.status === "l2_approved")
    .slice()
    .sort((a, b) => a.createdAt.localeCompare(b.createdAt));
  const completed = visible
    .filter((t) => t.status === "approved" || t.status === "denied")
    .slice()
    .sort((a, b) => b.createdAt.localeCompare(a.createdAt));

  const [flash, setFlash] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [submitOpen, setSubmitOpen] = useState(false);

  // Supervisors (l2+) can submit on behalf of any officer via the
  // in-modal employee picker even when their own account isn't
  // linked to an employee. Non-supervisors need their own linked
  // employee since they can only submit for themselves.
  const canSubmit = !!me?.employeeId || canReview;

  return (
    <div className="p-4 md:p-6 max-w-5xl mx-auto space-y-6">
      <header className="flex flex-col md:flex-row md:items-end md:justify-between gap-2">
        <div>
          <h1 className="text-xl font-semibold text-slate-900">{formName}</h1>
          <p className="text-sm text-slate-500">
            {canReview
              ? "Submissions from every officer in your agency. Approve or deny each pending row."
              : "Submit a new request with the button on the right. Your submissions and their status appear below."}
          </p>
        </div>
        <Button
          onClick={() => setSubmitOpen(true)}
          disabled={!canSubmit}
          title={canSubmit ? undefined : "Your account isn't linked to an employee record — a supervisor can link it in Employees."}
        >
          + New submission
        </Button>
      </header>

      {error && (
        <div className="rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
          {error}
        </div>
      )}
      {flash && (
        <div className="rounded border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-800">
          {flash}
        </div>
      )}

      {submitOpen && canSubmit && (
        <SubmitModal
          slug={slug}
          formName={formName}
          myEmployeeId={me?.employeeId ?? null}
          canPickEmployee={canReview}
          employees={employees.data ?? []}
          fields={fields.data ?? []}
          onClose={() => setSubmitOpen(false)}
          onSubmitted={() => {
            qc.invalidateQueries({ queryKey: ["timeoff", start, end] });
            setSubmitOpen(false);
            setError(null);
            setFlash(`${formName} submitted.`);
          }}
          onError={(m) => { setFlash(null); setError(m); }}
        />
      )}

      <RequestListCard
        title="Pending"
        items={pending}
        emptyText="Nothing pending."
        fields={fields.data ?? []}
        empById={empById}
        canReview={canReview}
        onDecided={(kind) => {
          qc.invalidateQueries({ queryKey: ["timeoff", start, end] });
          setError(null);
          setFlash(`Submission ${kind}.`);
        }}
        onError={(m) => { setFlash(null); setError(m); }}
      />

      <RequestListCard
        title="Completed"
        items={completed}
        emptyText="No completed submissions yet."
        fields={fields.data ?? []}
        empById={empById}
        canReview={false /* completed rows are read-only */}
        onDecided={() => { /* not reachable when canReview=false */ }}
        onError={() => { /* not reachable when canReview=false */ }}
      />
    </div>
  );
}

// ---------------------------------------------------------------
// Submit-a-new-request modal. Opened by the "+ New submission"
// button on the page header. Renders the form's dynamic field
// catalogue plus an optional note, then posts a time_off row with
// form_slug set. type stays "other" so the row never materialises
// as a schedule cell.

function SubmitModal({
  slug, formName, myEmployeeId, canPickEmployee, employees, fields,
  onClose, onSubmitted, onError,
}: {
  slug: string;
  formName: string;
  // Signed-in user's linked employee id, if any. Null when the
  // supervisor has no employee record — they must pick one below.
  myEmployeeId: string | null;
  // Supervisor override: when true the modal renders an employee
  // picker so an L2+ can submit on behalf of any officer. Defaults
  // to the user's own employee when they have one.
  canPickEmployee: boolean;
  employees: import("../lib/types").Employee[];
  fields: RequestField[];
  onClose: () => void;
  onSubmitted: () => void;
  onError: (message: string) => void;
}) {
  const [values, setValues] = useState<Record<string, string | number | boolean>>({});
  const [note, setNote] = useState("");
  const [inlineError, setInlineError] = useState<string | null>(null);
  // Employee this submission is being filed on. Defaults to the
  // signed-in user; a supervisor with the picker can override to
  // any officer. Falls back to the first non-inactive employee when
  // the supervisor has no linked employee of their own.
  //
  // Filter is `status !== "inactive"` (not strict "active"), so
  // officers on temporary `leave` still appear — a leave officer
  // can still file an overtime-claim / training request etc. The
  // live schedule uses the same loose filter.
  const activeEmployees = useMemo(
    () => employees.filter((e) => e.status !== "inactive"),
    [employees],
  );
  const [employeeId, setEmployeeId] = useState<string>(
    myEmployeeId ?? activeEmployees[0]?.id ?? "",
  );
  const showPicker = canPickEmployee;

  const submitMut = useMutation({
    mutationFn: async () => {
      if (!employeeId) throw new Error("Pick an officer to submit for.");
      // Required-field guard: refuse before hitting the wire so the
      // user gets an inline message instead of a Postgres constraint
      // error.
      for (const f of fields) {
        if (!f.required) continue;
        const v = values[f.key];
        const empty =
          v === undefined || v === null
          || (typeof v === "string" && v.trim() === "")
          || (f.fieldType === "checkbox" && v === false);
        if (empty) throw new Error(`"${f.label}" is required.`);
      }
      const today = ymd(new Date());
      return api.requestTimeOff({
        employeeId,
        startDate: today,
        endDate: today,
        type: "other",
        note: note.trim() || null,
        metadata: values,
        formSlug: slug,
      });
    },
    onSuccess: () => {
      setValues({});
      setNote("");
      setInlineError(null);
      onSubmitted();
    },
    onError: (e: { message?: string }) => {
      const msg = e?.message ?? "Couldn't submit.";
      setInlineError(msg);
      onError(msg);
    },
  });

  return (
    <Modal
      open={true}
      onClose={() => { if (!submitMut.isPending) onClose(); }}
      title={`Submit ${formName}`}
      footer={
        <>
          <Button
            variant="secondary"
            onClick={onClose}
            disabled={submitMut.isPending}
          >
            Cancel
          </Button>
          <Button
            onClick={() => { setInlineError(null); submitMut.mutate(); }}
            disabled={submitMut.isPending}
          >
            {submitMut.isPending ? "Submitting…" : "Submit"}
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        {showPicker && (
          <Field label="Submit for">
            <select
              className="w-full rounded border-slate-300 text-sm"
              value={employeeId}
              onChange={(e) => setEmployeeId(e.target.value)}
              disabled={submitMut.isPending}
            >
              {!employeeId && <option value="">Pick an officer…</option>}
              {activeEmployees
                .slice()
                .sort((a, b) => a.lastName.localeCompare(b.lastName)
                  || a.firstName.localeCompare(b.firstName))
                .map((e) => (
                  <option key={e.id} value={e.id}>
                    {e.lastName}, {e.firstName}
                    {myEmployeeId === e.id ? " (you)" : ""}
                  </option>
                ))}
            </select>
          </Field>
        )}
        {fields.length === 0 ? (
          <p className="text-sm text-slate-500">
            This form doesn't have any custom fields yet. You can still submit
            an empty request with a note — an admin can add fields later in{" "}
            <strong>Settings → Forms</strong>.
          </p>
        ) : (
          <RequestFieldList
            fields={fields}
            values={values}
            onChange={(k, v) => setValues((prev) => ({ ...prev, [k]: v }))}
          />
        )}
        <Field label="Note (optional)">
          <Textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Anything a reviewer should know."
            rows={2}
          />
        </Field>
        {inlineError && (
          <div className="rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
            {inlineError}
          </div>
        )}
      </div>
    </Modal>
  );
}

// ---------------------------------------------------------------
// Reusable list card for both "Pending" and "Completed" sections.

function RequestListCard({
  title, items, emptyText, fields, empById, canReview, onDecided, onError,
}: {
  title: string;
  items: TimeOff[];
  emptyText: string;
  fields: RequestField[];
  empById: Map<string, { firstName: string; lastName: string }>;
  canReview: boolean;
  onDecided: (kind: "approved" | "denied") => void;
  onError: (message: string) => void;
}) {
  const decideMut = useMutation({
    mutationFn: (args: { id: string; approve: boolean }) =>
      api.reviewTimeOff(args.id, args.approve),
    onSuccess: (_res, args) => onDecided(args.approve ? "approved" : "denied"),
    onError: (e: { message?: string }) =>
      onError(e?.message ?? "Couldn't record decision."),
  });

  return (
    <section className="bg-white border border-slate-200 rounded-lg p-5 space-y-3">
      <h2 className="font-medium text-slate-900">
        {title}
        {items.length > 0 && (
          <span className="ml-2 text-slate-500 font-normal">({items.length})</span>
        )}
      </h2>
      {items.length === 0 ? (
        <p className="text-sm text-slate-500">{emptyText}</p>
      ) : (
        <ul className="divide-y divide-slate-100">
          {items.map((t) => {
            const emp = empById.get(t.employeeId);
            return (
              <li key={t.id} className="py-3 flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium text-slate-900">
                    {emp ? `${emp.lastName}, ${emp.firstName}` : t.employeeId}
                    <span className={
                      "ml-2 text-[10px] font-semibold uppercase tracking-wider rounded border px-1.5 py-0.5 "
                      + (t.status === "approved" ? "bg-emerald-50 text-emerald-800 border-emerald-200"
                        : t.status === "denied" ? "bg-red-50 text-red-800 border-red-200"
                        : "bg-amber-50 text-amber-800 border-amber-200")
                    }>
                      {t.status}
                    </span>
                  </div>
                  {t.note && (
                    <div className="mt-1 text-xs italic text-slate-600">{t.note}</div>
                  )}
                  {fields.length > 0 && (
                    <dl className="mt-1 text-xs text-slate-600 grid grid-cols-1 md:grid-cols-2 gap-x-4">
                      {fields
                        .slice()
                        .sort((a, b) => a.sortOrder - b.sortOrder)
                        .map((f) => {
                          const v = t.metadata?.[f.key];
                          if (v === undefined || v === null || v === "") return null;
                          const shown = formatRequestFieldValue(f, v as string | number | boolean | null);
                          return (
                            <div key={f.id} className="flex gap-1">
                              <dt className="text-slate-500">{f.label}:</dt>
                              <dd className="text-slate-800">{shown}</dd>
                            </div>
                          );
                        })}
                    </dl>
                  )}
                  <div className="mt-1 text-[11px] text-slate-400">
                    Submitted {new Date(t.createdAt).toLocaleString()}
                  </div>
                </div>
                {canReview && (
                  <div className="flex items-center gap-2 shrink-0">
                    <Button
                      size="sm"
                      confirm="Confirm approve"
                      onClick={() => decideMut.mutate({ id: t.id, approve: true })}
                      disabled={decideMut.isPending}
                    >
                      Approve
                    </Button>
                    <Button
                      size="sm"
                      variant="danger"
                      confirm="Confirm deny"
                      onClick={() => decideMut.mutate({ id: t.id, approve: false })}
                      disabled={decideMut.isPending}
                    >
                      Deny
                    </Button>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
